import { useEffect } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { RoleType, PermissionLevel, ROLE_DISPLAY_NAMES } from "@shared/permissions";
import { Button } from "@/components/ui/button";

interface RequirePermissionProps {
  component: React.ComponentType;
  role: RoleType;
  minLevel?: PermissionLevel;
}

export default function RequirePermission({ component: Component, role, minLevel }: RequirePermissionProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { hasRole, hasPermission, isPending: permPending, isError, error, refetch } = usePermissions();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const returnUrl = encodeURIComponent(location);
      setLocation(`/login?returnUrl=${returnUrl}`);
    }
  }, [authLoading, isAuthenticated, location, setLocation]);

  const stillLoading = authLoading || (isAuthenticated && permPending);

  if (stillLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Checking permissions...</p>
      </div>
    );
  }

  if (isError) {
    const errAny = error as any;
    const isTimeout = errAny?.status === 504;
    const isNetwork = errAny?.status === 502;
    const headline = isTimeout
      ? "Connection timed out"
      : isNetwork
        ? "Service temporarily unavailable"
        : "Unable to verify permissions";
    const detail = isTimeout
      ? "The permissions service took too long to respond. Please try again."
      : isNetwork
        ? "We're having trouble connecting to the permissions service. This is usually temporary."
        : error?.message || "We couldn't verify your access. This may be a temporary issue.";

    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="text-2xl font-bold text-muted-foreground">{headline}</div>
        <p className="text-muted-foreground max-w-md">{detail}</p>
        <Button variant="outline" onClick={() => refetch()} data-testid="button-retry-permissions">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  const hasAccess = minLevel ? hasPermission(role, minLevel) : hasRole(role);
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="text-4xl font-bold text-muted-foreground">Access Denied</div>
        <p className="text-muted-foreground max-w-md">
          You don't have access to the {ROLE_DISPLAY_NAMES[role] || role} portal. 
          Contact your administrator for access.
        </p>
      </div>
    );
  }

  return <Component />;
}
